(function () {
  if (customElements.get('nco-upsell')) return;

  class NcoUpsell extends HTMLElement {
    constructor() {
      super();
      this.sectionId = 'nco-cart-upsell';
      this.rendering = false;
      this.subscriptions = [];
      this.flkty = null;
      this.io = null;
      this.pendingCard = null;
      this.pendingBtn = null;
    }

    connectedCallback() {
      this.context = this.dataset.context;
      this.count = parseInt(this.dataset.count, 10) || 3;
      this.source = this.dataset.source || 'most_recent';
      this.target = this.querySelector('[data-nco-upsell-target]');
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      if (this.context !== 'product') this.bindCartEvents();
      this.observeVisibility();
      this.render();
    }

    disconnectedCallback() {
      this.subscriptions.forEach(function (unsub) {
        if (typeof unsub === 'function') unsub();
      });
      this.subscriptions = [];
      this.destroyFlickity();
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
    }

    // Re-measure the slider when the element becomes visible
    // (cart drawer opening, collapsible tab expanding).
    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && self.flkty) self.flkty.resize();
        }
      });
      this.io.observe(this);
    }

    bindCartEvents() {
      if (!window.MinimogEvents || !window.MinimogTheme) return;
      var events = window.MinimogTheme.pubSubEvents;
      var self = this;
      this.subscriptions.push(
        window.MinimogEvents.subscribe(events.cartUpdate, function (data) {
          if (data && data.source === 'nco-upsell') return;
          self.render();
        })
      );
      this.subscriptions.push(
        window.MinimogEvents.subscribe(events.openCartDrawer, function () {
          self.render();
        })
      );
    }

    get root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    async getCart() {
      try {
        var res = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return { items: [] };
        return await res.json();
      } catch (e) {
        console.error('[nco-upsell] cart fetch failed', e);
        return { items: [] };
      }
    }

    pickSources(items) {
      if (!items.length) return [];
      var seen = {};
      var unique = [];
      items.forEach(function (i) {
        if (!seen[i.product_id]) {
          seen[i.product_id] = true;
          unique.push({ id: i.product_id, handle: i.handle });
        }
      });
      if (this.source === 'first') {
        return [{ id: items[0].product_id, handle: items[0].handle }];
      }
      if (this.source === 'all') {
        return unique.slice(0, 5);
      }
      var last = items[items.length - 1];
      return [{ id: last.product_id, handle: last.handle }];
    }

    async fetchSection(url) {
      try {
        var res = await fetch(url);
        if (!res.ok) return [];
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('[data-nco-upsell-card]'));
      } catch (e) {
        console.error('[nco-upsell] section fetch failed', e);
        return [];
      }
    }

    // Fetches the section via the product route, which renders the
    // keyword-matched and static fallback products from theme settings.
    fetchProductRoute(handle) {
      if (!handle) return Promise.resolve([]);
      return this.fetchSection(
        this.root + 'products/' + encodeURIComponent(handle) + '?section_id=' + this.sectionId
      );
    }

    fetchRecs(productId, intent) {
      var limit = Math.min(this.count + 4, 10);
      return this.fetchSection(
        this.root + 'recommendations/products'
        + '?section_id=' + this.sectionId
        + '&intent=' + intent
        + '&limit=' + limit
        + '&product_id=' + productId
      );
    }

    // Priority order per source product: Shopify complementary first, then
    // keyword rules + static fallback products from the theme settings.
    // Both routes are always fetched so manual sources stay available even
    // when complementary cards get filtered out (in cart, source, sold out).
    async collectCards(sources) {
      var cards = [];
      for (var i = 0; i < sources.length; i++) {
        var src = sources[i];
        var complementary = await this.fetchRecs(src.id, 'complementary');
        var manual = await this.fetchProductRoute(src.handle);
        cards = cards.concat(complementary).concat(manual);
      }
      return cards;
    }

    async render() {
      if (this.rendering) return;
      this.rendering = true;
      try {
        var cart = await this.getCart();
        var items = cart.items || [];
        var inCart = new Set(items.map(function (i) { return i.product_id; }));
        var sources;
        if (this.context === 'product') {
          sources = [{
            id: parseInt(this.dataset.productId, 10),
            handle: this.dataset.productHandle || ''
          }];
        } else {
          sources = this.pickSources(items);
        }
        if (!sources.length) { this.hideBlock(); return; }
        var sourceIds = sources.map(function (s) { return s.id; });

        var cards = await this.collectCards(sources);
        var seen = new Set();
        var chosen = [];
        for (var i = 0; i < cards.length; i++) {
          var pid = parseInt(cards[i].dataset.productId, 10);
          if (seen.has(pid) || inCart.has(pid) || sourceIds.indexOf(pid) !== -1) continue;
          seen.add(pid);
          chosen.push(cards[i]);
          if (chosen.length >= this.count) break;
        }
        if (!chosen.length) { this.hideBlock(); return; }
        this.destroyFlickity();
        this.target.replaceChildren.apply(this.target, chosen);
        var self = this;
        chosen.forEach(function (card) { self.syncCard(card); });
        this.hidden = false;
        this.toggleWrapper(true);
        this.initFlickity(chosen.length);
      } finally {
        this.rendering = false;
      }
    }

    hideBlock() {
      this.destroyFlickity();
      this.target.replaceChildren();
      this.hidden = true;
      this.toggleWrapper(false);
    }

    toggleWrapper(show) {
      var wrapper = this.closest('[data-nco-upsell-block]');
      if (wrapper) wrapper.hidden = !show;
    }

    initFlickity(cardCount) {
      if (this.dataset.layout === 'stacked') return;
      if (cardCount < 2 || typeof window.Flickity !== 'function') return;
      var self = this;
      this.flkty = new window.Flickity(this.target, {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        adaptiveHeight: true,
        groupCells: false
      });
      var nav = this.querySelector('[data-nco-upsell-nav]');
      if (nav) {
        nav.hidden = false;
        this.flkty.on('select', function () { self.updateNav(); });
      }
      this.updateNav();
    }

    destroyFlickity() {
      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }
      var nav = this.querySelector('[data-nco-upsell-nav]');
      if (nav) nav.hidden = true;
    }

    // Fade the prev/next arrows at the slider's ends.
    updateNav() {
      if (!this.flkty) return;
      var prev = this.querySelector('[data-nco-upsell-prev]');
      var next = this.querySelector('[data-nco-upsell-next]');
      var idx = this.flkty.selectedIndex;
      var last = (this.flkty.slides ? this.flkty.slides.length : 1) - 1;
      if (prev) prev.disabled = idx <= 0;
      if (next) next.disabled = idx >= last;
    }

    variantsOf(card) {
      var el = card.querySelector('[data-nco-upsell-variants]');
      try { return el ? JSON.parse(el.textContent) : []; }
      catch (e) { return []; }
    }

    selectedValues(card) {
      return Array.from(card.querySelectorAll('[data-nco-upsell-option]'))
        .sort(function (a, b) { return a.dataset.optionPosition - b.dataset.optionPosition; })
        .map(function (s) { return s.value; });
    }

    matchVariant(card) {
      var variants = this.variantsOf(card);
      if (!variants.length) return null;
      var values = this.selectedValues(card);
      if (!values.length) return variants[0];
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.options.length === values.length &&
            v.options.every(function (o, idx) { return o === values[idx]; })) {
          return v;
        }
      }
      return null;
    }

    syncCard(card) {
      var btn = card.querySelector('[data-nco-upsell-add]');
      if (btn.classList.contains('is-added')) return;
      var variant = this.matchVariant(card);
      if (!variant) { btn.disabled = true; return; }
      card.dataset.variantId = variant.id;
      btn.disabled = !variant.available;
      var prices = card.querySelectorAll('[data-nco-upsell-price] [data-variant-id]');
      prices.forEach(function (el) {
        el.hidden = parseInt(el.dataset.variantId, 10) !== variant.id;
      });
    }

    onChange(e) {
      var select = e.target.closest('[data-nco-upsell-option]');
      if (!select) return;
      this.syncCard(select.closest('[data-nco-upsell-card]'));
      if (this.flkty) this.flkty.resize();
    }

    onClick(e) {
      if (e.target.closest('[data-nco-upsell-prev]')) {
        if (this.flkty) this.flkty.previous();
        return;
      }
      if (e.target.closest('[data-nco-upsell-next]')) {
        if (this.flkty) this.flkty.next();
        return;
      }
      var sizeBtn = e.target.closest('[data-nco-upsell-card-size]');
      if (sizeBtn) {
        this.pickCardSize(sizeBtn);
        return;
      }
      var addBtn = e.target.closest('[data-nco-upsell-add]');
      if (addBtn && !addBtn.disabled && !addBtn.classList.contains('is-added')) {
        this.startAdd(addBtn);
      }
    }

    // Products with a single (default) variant add directly. Anything with
    // multiple variants opens the inline size picker, even if only one of
    // them is currently in stock — so the customer always picks a size.
    startAdd(addBtn) {
      var card = addBtn.closest('[data-nco-upsell-card]');
      var variants = this.variantsOf(card);
      if (variants.length <= 1) {
        this.addVariant(variants[0], card, addBtn);
        return;
      }
      var available = variants.filter(function (v) { return v.available; });
      var sizesEl = card.querySelector('[data-nco-upsell-card-sizes]');
      if (sizesEl && !sizesEl.hidden) {
        this.closeCardSizes();
        return;
      }
      this.openCardSizes(card, addBtn, available);
    }

    openCardSizes(card, addBtn, available) {
      var sizesEl = card.querySelector('[data-nco-upsell-card-sizes]');
      if (!sizesEl) { this.addVariant(available[0], card, addBtn); return; }
      // Collapse any other open size picker in this upsell.
      this.querySelectorAll('[data-nco-upsell-card-sizes]').forEach(function (el) {
        if (el !== sizesEl) el.hidden = true;
      });
      this.pendingCard = card;
      this.pendingBtn = addBtn;
      sizesEl.replaceChildren();
      available.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nco-upsell-card__size';
        b.setAttribute('data-nco-upsell-card-size', '');
        b.dataset.variantId = v.id;
        b.textContent = v.options.join(' / ');
        sizesEl.appendChild(b);
      });
      sizesEl.hidden = false;
      if (this.flkty) this.flkty.resize();
    }

    pickCardSize(sizeBtn) {
      var vid = parseInt(sizeBtn.dataset.variantId, 10);
      var card = this.pendingCard;
      var btn = this.pendingBtn;
      var variant = card
        ? this.variantsOf(card).filter(function (v) { return v.id === vid; })[0]
        : null;
      this.closeCardSizes();
      if (variant && card && btn) this.addVariant(variant, card, btn);
    }

    closeCardSizes() {
      if (this.pendingCard) {
        var sizesEl = this.pendingCard.querySelector('[data-nco-upsell-card-sizes]');
        if (sizesEl) sizesEl.hidden = true;
      }
      this.pendingCard = null;
      this.pendingBtn = null;
      if (this.flkty) this.flkty.resize();
    }

    async addVariant(variant, card, btn) {
      if (!variant || !variant.available) return;
      btn.classList.add('is-loading');
      try {
        // Ask Shopify to also re-render Minimog's cart sections in the response,
        // so we can update the cart drawer directly afterwards.
        var drawer = document.querySelector('m-cart-drawer');
        var sectionIds = [];
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          sectionIds = drawer.getSectionsToRender().map(function (s) { return s.id; });
        }
        var url = this.root + 'cart/add.js';
        if (sectionIds.length) {
          url += '?sections=' + encodeURIComponent(sectionIds.join(','));
        }
        var res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);
        var data = await res.json();
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.disabled = true;
        var label = btn.querySelector('.nco-upsell-card__add-label');
        if (label) label.textContent = btn.dataset.addedLabel;

        // Push the freshly-rendered sections into the cart drawer (Minimog's
        // own update path); a cartUpdate event covers other subscribers.
        if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
          drawer.renderContents(data);
        }
        if (window.MinimogEvents && window.MinimogTheme) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { source: 'nco-upsell' });
        }
      } catch (err) {
        btn.classList.remove('is-loading');
        console.error('[nco-upsell] add to cart failed', err);
      }
    }
  }

  customElements.define('nco-upsell', NcoUpsell);
})();
